import { createApi } from "@reduxjs/toolkit/query/react";
import { apiFunction } from "./utils";
import { baseQueryWithReauth } from "./baseQueryWithReauth";
import { serializeArgsWithoutToken } from "./serializeQueryArgs";

const authApi = createApi({
  reducerPath: "auth",
  baseQuery: baseQueryWithReauth,
  serializeQueryArgs: serializeArgsWithoutToken, // #142: token önbellek anahtarına girmez
  tagTypes: ["Auth"],
  keepUnusedDataFor: 60,
  endpoints(builder) {
    return {
      forgotPassword: builder.mutation({
        query: (email) => ({
          url: `/api/users/forgot-password`,
          method: "POST",
          body: { email },
        }),
      }),
      validateResetPasswordToken: builder.mutation({
        query: (token) => ({
          url: "/api/users/reset-password/validate",
          method: "POST",
          body: { token },
        }),
      }),
      resetPassword: builder.mutation({
        query: ({ token, code, newPassword, passwordConfirm }) => ({
          url: "/api/users/reset-password",
          method: "POST",
          body: { token, code, newPassword, passwordConfirm },
        }),
      }),
      // Davet bağlantısı durumu (#223): valid | expired | invalid. Davet
      // sayfası açılışta sorar; token body'de.
      checkInvitationStatus: builder.mutation({
        query: ({ token }) => ({
          url: "/api/users/invitation-status",
          method: "POST",
          body: { token },
        }),
      }),
      acceptInvitation: builder.mutation({
        query: ({ token, newPassword, passwordConfirm }) => ({
          url: "/api/users/accept-invitation",
          method: "POST",
          body: { token, newPassword, passwordConfirm },
        }),
      }),
      login: builder.mutation({
        query: (data) => apiFunction(data, `/api/users/login`, "POST"),
      }),
      verify2fa: builder.mutation({
        query: (data) => apiFunction(data, `/api/users/verify-2fa`, "POST"),
      }),
      sendSecondaryCode: builder.mutation({
        query: ({ tempToken, method }) => ({
          url: "/api/users/send-secondary-code",
          method: "POST",
          body: { tempToken, method },
        }),
      }),
      verifySecondaryCode: builder.mutation({
        query: ({ tempToken, code, deviceLabel }) => ({
          url: "/api/users/verify-secondary",
          method: "POST",
          body: { tempToken, code, ...(deviceLabel != null && deviceLabel !== "" ? { deviceLabel: String(deviceLabel).trim().slice(0, 100) } : {}) },
        }),
      }),
      logout: builder.query({
        query: ({ token }) => {
          return {
            url: `/api/users/logout`,
            headers: {
              Authorization: `Bearer ${token}`,
            },
            method: "GET",
          };
        },
      }),
      getTrustedDevices: builder.query({
        query: () => ({
          url: "/api/users/devices",
          method: "GET",
        }),
        providesTags: ["Auth"],
      }),
      updateTrustedDeviceLabel: builder.mutation({
        query: ({ id, label }) => ({
          url: `/api/users/devices/${id}`,
          method: "PATCH",
          body: { label: label ?? "" },
        }),
        invalidatesTags: ["Auth"],
      }),
      revokeTrustedDevice: builder.mutation({
        query: (id) => ({
          url: `/api/users/devices/${id}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Auth"],
      }),
      get2faSetup: builder.query({
        query: (token) => ({
          url: "/api/users/2fa/setup",
          method: "GET",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }),
      }),
      confirm2fa: builder.mutation({
        query: ({ code, token }) => ({
          url: "/api/users/2fa/confirm",
          method: "POST",
          body: { code },
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }),
        invalidatesTags: ["Auth"],
      }),
      get2faSetupWithToken: builder.mutation({
        query: (tempToken) => ({
          url: "/api/users/2fa/setup-with-token",
          method: "POST",
          body: { tempToken },
        }),
      }),
      complete2faSetup: builder.mutation({
        query: ({ tempToken, code }) => ({
          url: "/api/users/2fa/complete-setup",
          method: "POST",
          body: { tempToken, code },
        }),
      }),
      registerDeviceReplacing: builder.mutation({
        query: ({ choiceToken, replaceDeviceId, deviceLabel }) => ({
          url: "/api/users/devices/register-replacing",
          method: "POST",
          body: { choiceToken, replaceDeviceId, ...(deviceLabel != null && deviceLabel !== "" ? { deviceLabel: String(deviceLabel).trim().slice(0, 100) } : {}) },
        }),
      }),
      verifyAndStart2faChange: builder.mutation({
        query: ({ code, token }) => ({
          url: "/api/users/2fa/verify-and-start-change",
          method: "POST",
          body: { code },
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }),
      }),
      send2faChangeEmailCode: builder.mutation({
        query: (token) => ({
          url: "/api/users/2fa/send-change-email-code",
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }),
      }),
      verify2faChangeByEmailCode: builder.mutation({
        query: ({ code, token }) => ({
          url: "/api/users/2fa/verify-email-and-start-change",
          method: "POST",
          body: { code },
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }),
      }),
      confirm2faChange: builder.mutation({
        query: ({ code, token }) => ({
          url: "/api/users/2fa/confirm-change",
          method: "POST",
          body: { code },
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }),
        invalidatesTags: ["Auth"],
      }),
      sendPasswordChangeEmailCode: builder.mutation({
        query: (token) =>
          token
            ? { url: "/api/users/send-password-change-email-code", method: "POST", headers: { Authorization: `Bearer ${token}` } }
            : { url: "/api/users/send-password-change-email-code", method: "POST" },
      }),
      updateMyPassword: builder.mutation({
        query: (input) =>
          apiFunction(input, `/api/users/update-my-password`, "PATCH"),
        invalidatesTags: ["Auth"],
      }),
      updateMe: builder.mutation({
        query: (input) =>
          apiFunction(input, `/api/users/update-me`, "PATCH"),
        invalidatesTags: ["Auth"],
      }),
      // Profil e-posta değiştirme (panel-profile 1.0): kod yeni adrese gider.
      sendEmailChangeCode: builder.mutation({
        query: (input) =>
          apiFunction(input, `/api/users/email-change/send-code`, "POST"),
      }),
      confirmEmailChange: builder.mutation({
        query: (input) =>
          apiFunction(input, `/api/users/email-change/confirm`, "POST"),
        invalidatesTags: ["Auth"],
      }),
    };
  },
});

export const {
  useForgotPasswordMutation,
  useValidateResetPasswordTokenMutation,
  useResetPasswordMutation,
  useAcceptInvitationMutation,
  useCheckInvitationStatusMutation,
  useLoginMutation,
  useVerify2faMutation,
  useSendSecondaryCodeMutation,
  useVerifySecondaryCodeMutation,
  useLogoutQuery,
  useLazyLogoutQuery,
  useGetTrustedDevicesQuery,
  useUpdateTrustedDeviceLabelMutation,
  useRevokeTrustedDeviceMutation,
  useLazyGet2faSetupQuery,
  useConfirm2faMutation,
  useGet2faSetupWithTokenMutation,
  useComplete2faSetupMutation,
  useRegisterDeviceReplacingMutation,
  useVerifyAndStart2faChangeMutation,
  useSend2faChangeEmailCodeMutation,
  useVerify2faChangeByEmailCodeMutation,
  useConfirm2faChangeMutation,
  useSendPasswordChangeEmailCodeMutation,
  useUpdateMyPasswordMutation,
  useUpdateMeMutation,
  useSendEmailChangeCodeMutation,
  useConfirmEmailChangeMutation,
} = authApi;
export { authApi };
